/**
 * Deterministic PRNG. The engine must be reproducible from a seed alone —
 * `sim:replay <seed>` running twice has to produce byte-identical output —
 * so nothing in the engine may call Math.random().
 *
 * mulberry32: small, fast, good enough distribution for shuffling a deck.
 */
export interface Rng {
  /** Float in [0, 1). */
  next(): number;
  /** Integer in [0, maxExclusive). */
  nextInt(maxExclusive: number): number;
  /** Current internal state, so a snapshot can be resumed exactly. */
  getState(): number;
}

export function createRng(seed: number): Rng {
  let state = seed >>> 0;

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  return {
    next,
    nextInt: (maxExclusive) => (maxExclusive <= 0 ? 0 : Math.floor(next() * maxExclusive)),
    getState: () => state,
  };
}

/**
 * Fisher-Yates, driven by the supplied Rng. Returns a new array; the input is
 * left alone so callers can keep an unshuffled reference for tests.
 */
export function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const a = out[i]!;
    const b = out[j]!;
    out[i] = b;
    out[j] = a;
  }
  return out;
}
